import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, ClipboardList, Package, AlertTriangle, CheckCircle } from 'lucide-react';

const ProcurementPlanning = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/procurement-planning');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to fetch procurement planning data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  }

  if (!data) {
    return <div className="text-center py-12 text-slate-500">Failed to load data</div>;
  }

  const { active_pos_count, parts_required, material_requirements, po_summaries } = data;
  const totalShortfall = (material_requirements || []).filter(m => m.shortfall > 0).length || 0;
  const totalSurplus = (material_requirements || []).filter(m => m.surplus > 0).length || 0;

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="procurement-planning">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-sky-500" />
            Material Procurement Planning
          </h1>
          <p className="text-slate-500 text-sm">Based on active customer purchase orders</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="rounded-sm" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-sm"><ClipboardList className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Active POs</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="active-pos-count">{active_pos_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-sm"><Package className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Parts Required</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="parts-count">{(parts_required ? parts_required.length : 0) || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-sm"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Materials Short</p>
              <p className="text-2xl font-bold text-red-600" data-testid="shortfall-count">{totalShortfall}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-sm"><CheckCircle className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Materials OK</p>
              <p className="text-2xl font-bold text-green-600" data-testid="surplus-count">{totalSurplus}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active PO Summary */}
      <Card className="mb-6 border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Active Customer POs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(po_summaries ? po_summaries.length : 0) === 0 ? (
            <div className="text-center py-8 text-slate-500">No active customer POs found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>CPO No</TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Delivery Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pending Parts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(po_summaries || []).map((po) => (
                    <TableRow key={po.po_id}>
                      <TableCell className="font-mono font-bold">CPO-{po.po_no}</TableCell>
                      <TableCell>{po.po_number}</TableCell>
                      <TableCell className="text-sm">{po.customer_name}</TableCell>
                      <TableCell>{po.delivery_date ? new Date(po.delivery_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          po.status === 'ACTIVE' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                        }`}>{po.status}</span>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {(po.parts || []).map((p, i) => (
                            <div key={i} className="text-sm">
                              <span className="font-mono text-slate-600">{p.part_no}</span>
                              <span className="ml-2 text-orange-600 font-medium">{p.pending_quantity.toFixed(0)} pending</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Parts Required */}
      {(parts_required ? parts_required.length : 0) > 0 && (
        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Parts Required (Consolidated)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Part No</TableHead>
                    <TableHead>Part Name</TableHead>
                    <TableHead className="text-right">Total Pending</TableHead>
                    <TableHead>PO References</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parts_required.map((p) => (
                    <TableRow key={p.part_id}>
                      <TableCell className="font-mono">{p.part_no}</TableCell>
                      <TableCell>{p.part_name}</TableCell>
                      <TableCell className="text-right font-mono font-bold">{p.total_pending.toFixed(0)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {(p.po_references || []).map((ref, i) => (
                            <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono">{ref}</span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Material Requirements */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Raw Material Requirements (via BOM)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(material_requirements ? material_requirements.length : 0) === 0 ? (
            <div className="text-center py-8 text-slate-500">
              {(parts_required ? parts_required.length : 0) > 0
                ? 'No BOM data found for the required parts. Please ensure BOM is configured.'
                : 'No active POs with pending parts.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Item Code</TableHead>
                    <TableHead>Item Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead className="text-right">Required</TableHead>
                    <TableHead className="text-right">In Stock</TableHead>
                    <TableHead className="text-right">Shortfall</TableHead>
                    <TableHead className="text-right">Surplus</TableHead>
                    <TableHead>For Parts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(material_requirements || []).map((m) => (
                    <TableRow key={m.item_id} className={m.shortfall > 0 ? 'bg-red-50/50' : ''}>
                      <TableCell className="font-mono">{m.item_code}</TableCell>
                      <TableCell>{m.item_name}</TableCell>
                      <TableCell className="text-sm">{m.item_type}</TableCell>
                      <TableCell>{m.uom}</TableCell>
                      <TableCell className="text-right font-mono font-bold">{m.total_required.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">{m.current_stock.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">
                        {m.shortfall > 0 ? (
                          <span className="text-red-600 font-bold flex items-center justify-end gap-1">
                            <AlertTriangle className="w-3 h-3" /> {m.shortfall.toFixed(2)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {m.surplus > 0 ? <span className="text-green-600">{m.surplus.toFixed(2)}</span> : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          {(m.for_parts || []).map((fp, i) => (
                            <div key={i} className="text-xs text-slate-600">
                              {fp.part_no}: {fp.quantity_needed.toFixed(2)}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProcurementPlanning;
